import { useEffect, type RefObject } from "react";
import { isTauri } from "@/lib/is-tauri";
import { listenWhileMounted } from "./tauri-listener-cleanup";
import {
  advanceTrayPanelPressGate,
  trayPopoverContainsPress,
} from "./tray-panel-geometry";
import { getWindowOrigin } from "./use-window-origin";
import { getWindowDisplayId } from "./use-window-role";

type CursorPress = {
  displayId?: number;
  x?: number;
  y?: number;
  primaryDown?: boolean;
};

/** Click-through presses in other apps never produce DOM pointer events. */
export function useScreenRecordingPreviewNativeDismiss({
  anchorRef,
  contentRef,
  onClose,
}: {
  anchorRef: RefObject<HTMLElement | null>;
  contentRef: RefObject<HTMLElement | null>;
  onClose: () => void;
}) {
  useEffect(() => {
    window.addEventListener("blur", onClose);
    let readyForNewPress = false;
    const stop = isTauri()
      ? listenWhileMounted<CursorPress>("cursor-position", ({ payload }) => {
          const gate = advanceTrayPanelPressGate(
            readyForNewPress,
            payload.primaryDown === true,
          );
          readyForNewPress = gate.readyForNewPress;
          if (
            !gate.shouldCheckOutside ||
            typeof payload.x !== "number" ||
            typeof payload.y !== "number"
          )
            return;
          const origin = getWindowOrigin();
          if (!origin.ready) return;
          const inside = trayPopoverContainsPress({
            panelDisplayId: getWindowDisplayId(),
            pressDisplayId: payload.displayId,
            x: payload.x - origin.x,
            y: payload.y - origin.y,
            popovers: [contentRef.current, anchorRef.current].flatMap(
              (element) => (element ? [element.getBoundingClientRect()] : []),
            ),
          });
          if (!inside) onClose();
        })
      : null;
    return () => {
      window.removeEventListener("blur", onClose);
      stop?.();
    };
  }, [anchorRef, contentRef, onClose]);
}
